import { EyeIcon, PresentationFillIcon } from "@vericus/cadmus-icons";
import { Button, Text } from "@vericus/cadmus-ui";

import { useParams } from "react-router-dom";

import { getFinalDate } from "@/features/assignment";
import {
  AssessmentFragment,
  useAssessmentQuery,
  useWorkGroupQuery,
  WorkSettingsFragment,
} from "@/generated/graphql";
import { useRootNavigate } from "@/routes";
import { formatDateFull } from "@/utils/datetime";

import { ExtensionPill } from "./extension-pill";
import { GroupCard } from "./group-card";
import { GroupCharterCard } from "./group-charter-card";
import * as styles from "./home-page.css";

export function Preparation() {
  const { assessmentId = "" } = useParams();
  const { data: assessmentData } = useAssessmentQuery({
    variables: { assessmentId },
  });
  const { data: workGroupData } = useWorkGroupQuery({
    variables: { assessmentId },
  });

  const assessment = assessmentData?.assessment;
  const workGroup = workGroupData?.studentWorkGroup;
  const group = workGroup?.group;

  if (!assessment) {
    return null;
  }

  // Charter card only shows once the charter is usable (enabled AND a published
  // form exists), folded server-side into `groupCharterAvailable`.
  const showCharter = assessment.settings?.groupCharterAvailable ?? false;
  return (
    <>
      <div className={styles.verticalLine} />
      <GroupCard
        assessment={assessment}
        workGroupId={workGroup?.id}
        userGroup={group ?? null}
        finalised={workGroup?.finalised ?? false}
        changeRequested={workGroup?.latestGroupChangeRequest}
      />
      {group && showCharter && (
        <GroupCharterCard
          groupId={group.id}
          charter={workGroup?.charter}
          finalised={workGroup?.finalised}
          dueDate={assessment.settings?.groupCharterDueDate}
        />
      )}
      <InstructionCard
        assessment={assessment}
        groupWorkSettings={workGroup?.work?.settings}
        individualWorkSettings={workGroup?.individualWork?.settings}
      />
    </>
  );
}

interface InstructionProps {
  assessment: AssessmentFragment;
  groupWorkSettings?: WorkSettingsFragment | null;
  individualWorkSettings?: WorkSettingsFragment | null;
}

function InstructionCard(props: InstructionProps) {
  const { assessment, groupWorkSettings, individualWorkSettings } = props;
  const rootNavigate = useRootNavigate();

  const [groupDueDate, groupIsExtended] = getFinalDate({
    settings: assessment.settings ?? {},
    workSettings: groupWorkSettings ?? null,
    isGroupWork: true,
  });
  const groupOriginalDue = assessment.settings?.dueDate ?? null;

  const [individualDueDate, individualIsExtended] = getFinalDate({
    settings: assessment.settings ?? {},
    workSettings: individualWorkSettings ?? null,
  });
  const individualOriginalDue =
    assessment.settings?.individualReportDueDate ??
    assessment.settings?.dueDate ??
    null;

  return (
    <div className={styles.steps}>
      <div className={styles.stepIcon["initial"]}>
        <PresentationFillIcon label="" size={30} />
      </div>
      <div className={styles.stepCard}>
        <Text kind="headingFour">Instructions</Text>
        <Text kind="bodyLine">
          Instructions for completing your report are available here.
        </Text>
        <div className={styles.instructionSheet}>
          <div>
            <Text kind="headingFive" textTransform="capitalize">
              Group Work: {assessment.name}
            </Text>
            {groupDueDate !== null && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Text kind="bodySm">
                  <strong>Due:</strong> {formatDateFull(new Date(groupDueDate))}
                </Text>
                {groupIsExtended && groupOriginalDue !== null && (
                  <ExtensionPill
                    originalDueDate={groupOriginalDue}
                    extendedDueDate={groupDueDate}
                  />
                )}
              </div>
            )}
          </div>
          <Button
            kind="secondary"
            icon={<EyeIcon label="" size={24} />}
            onClick={() => rootNavigate("/instructions/group")}
          >
            View Instructions
          </Button>
        </div>
        {assessment.settings?.individualReportEnabled === true && (
          <div className={styles.instructionSheet}>
            <div>
              <Text kind="headingFive">Individual Work</Text>
              {individualDueDate !== null && (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Text kind="bodySm">
                    <strong>Due:</strong>{" "}
                    {formatDateFull(new Date(individualDueDate))}
                  </Text>
                  {individualIsExtended && individualOriginalDue !== null && (
                    <ExtensionPill
                      originalDueDate={individualOriginalDue}
                      extendedDueDate={individualDueDate}
                    />
                  )}
                </div>
              )}
            </div>
            <Button
              kind="secondary"
              icon={<EyeIcon label="" size={24} />}
              onClick={() => rootNavigate("/instructions/individual")}
            >
              View Instructions
            </Button>
          </div>
        )}
        {assessment.settings?.peerEvaluationEnabled === true && (
          <div className={styles.instructionSheet}>
            <div>
              <Text kind="headingFive">Peer Evaluation</Text>
              {assessment.settings?.peerEvaluationDueDate != null && (
                <Text kind="bodySm">
                  <strong>Due:</strong>{" "}
                  {formatDateFull(
                    new Date(assessment.settings.peerEvaluationDueDate)
                  )}
                </Text>
              )}
            </div>
            <Button
              kind="secondary"
              icon={<EyeIcon label="" size={24} />}
              onClick={() => rootNavigate("/instructions/peer")}
            >
              View Instructions
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
